import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiErrorMessage } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  AlertTriangle, Clock, CheckCircle2, ChevronRight, Zap, RefreshCw,
  Activity,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_STYLES = {
  open: "bg-amber-500/15 text-amber-300 border-amber-500/40",
  in_progress: "bg-indigo-500/15 text-indigo-300 border-indigo-500/40",
  completed: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
};

const PRIORITY_STYLES = {
  emergency: "bg-rose-500 text-white",
  high: "bg-orange-500 text-white",
  medium: "bg-amber-500 text-black",
  low: "bg-zinc-700 text-zinc-200",
};

function relTime(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

const FilterPill = ({ value, label, count, active, onSelect }) => (
  <button
    onClick={() => onSelect(value)}
    className={
      "px-3 py-1.5 rounded-full text-xs font-medium border transition " +
      (active
        ? "bg-orange-500 border-orange-500 text-white"
        : "bg-white/[0.04] border-white/10 text-white/70 hover:bg-white/[0.08]")
    }
    data-testid={`tech-filter-${value}`}
  >
    {label}
    {count !== undefined && (
      <span className="ml-1.5 opacity-70 tabular-nums">{count}</span>
    )}
  </button>
);

export default function TechHomePage() {
  const nav = useNavigate();
  const [items, setItems] = useState(null);
  const [tallies, setTallies] = useState({});
  const [filter, setFilter] = useState("all");
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const { data } = await api.get("/tech/tickets", { params: { status: filter } });
      setItems(data.items || []);
      setTallies(data.tallies || {});
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Failed to load tickets");
      setItems([]);
    } finally {
      setRefreshing(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="tech-home">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-orange-400">Your queue</div>
          <h1 className="text-2xl font-semibold font-display">Tickets</h1>
        </div>
        <Button size="sm" variant="ghost"
                onClick={load} disabled={refreshing}
                className="text-white/70 hover:text-white hover:bg-white/10"
                data-testid="tech-refresh-btn">
          <RefreshCw className={"h-4 w-4 " + (refreshing ? "animate-spin" : "")} />
        </Button>
      </div>

      {/* Pace Board nav — one tap to see monthly performance */}
      <button
        onClick={() => nav("/tech/pace")}
        className="w-full mb-4 rounded-lg border border-orange-500/30 bg-orange-500/[0.06] hover:bg-orange-500/[0.10] transition p-3 flex items-center gap-3 active:scale-[0.99]"
        data-testid="tech-pace-nav">
        <Activity className="h-4 w-4 text-orange-400 shrink-0" />
        <div className="flex-1 text-left">
          <div className="text-sm font-medium text-white">See my pace this month</div>
          <div className="text-[11px] text-white/60">Tickets closed · accept time · photo attach rate</div>
        </div>
        <ChevronRight className="h-4 w-4 text-white/40" />
      </button>

      {/* Escalation banner — critical warning */}
      {(tallies.escalated || 0) > 0 && (
        <Card className="p-3 mb-4 bg-rose-500/10 border-rose-500/30 flex items-center gap-3"
              data-testid="tech-escalated-banner">
          <AlertTriangle className="h-5 w-5 text-rose-400 shrink-0" />
          <div className="text-sm text-rose-200">
            <span className="font-semibold">{tallies.escalated}</span> ticket
            {tallies.escalated !== 1 ? "s" : ""} escalated to PM
          </div>
        </Card>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-4" data-testid="tech-filters">
        <FilterPill value="all" label="All" active={filter === "all"} onSelect={setFilter} />
        <FilterPill value="open" label="Open" count={tallies.open} active={filter === "open"} onSelect={setFilter} />
        <FilterPill value="in_progress" label="In progress" count={tallies.in_progress} active={filter === "in_progress"} onSelect={setFilter} />
        <FilterPill value="completed" label="Done" count={tallies.completed} active={filter === "completed"} onSelect={setFilter} />
      </div>

      {/* Ticket cards */}
      {items === null ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="rounded-lg bg-white/[0.03] animate-pulse h-28" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <Card className="p-8 bg-white/[0.03] border-white/10 text-center" data-testid="tech-empty">
          <CheckCircle2 className="h-10 w-10 text-emerald-500/70 mx-auto mb-2" />
          <div className="text-lg font-medium">You&apos;re all caught up.</div>
          <div className="text-sm text-white/60 mt-1">
            New tickets will appear here as they come in.
          </div>
        </Card>
      ) : (
        <div className="space-y-3" data-testid="tech-list">
          {items.map((t) => {
            const unaccepted = t.assigned_to && !t.acknowledged_at && t.status === "open";
            return (
              <button
                key={t.id}
                onClick={() => nav(`/tech/ticket/${t.id}`)}
                className="w-full text-left rounded-lg border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] transition p-4 active:scale-[0.99]"
                data-testid={`tech-ticket-${t.id}`}
              >
                <div className="flex items-start gap-2 mb-2">
                  <Badge className={PRIORITY_STYLES[t.priority] || PRIORITY_STYLES.medium}>
                    {t.priority}
                  </Badge>
                  <Badge variant="outline" className={STATUS_STYLES[t.status] || STATUS_STYLES.open}>
                    {(t.status || "open").replace("_", " ")}
                  </Badge>
                  {t.escalated_at && (
                    <Badge className="bg-rose-500/20 text-rose-300 border-rose-500/40" data-testid={`tech-escalated-${t.id}`}>
                      <Zap className="h-3 w-3 mr-0.5" /> escalated
                    </Badge>
                  )}
                  {unaccepted && (
                    <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/40" data-testid={`tech-unaccepted-${t.id}`}>
                      <Clock className="h-3 w-3 mr-0.5" /> accept me
                    </Badge>
                  )}
                </div>
                <div className="font-medium text-white truncate">{t.title || "Maintenance ticket"}</div>
                <div className="text-xs text-white/50 mt-0.5 flex items-center gap-2 flex-wrap">
                  <span>Unit {t.unit || "—"}</span>
                  <span>·</span>
                  <span>{t.category}</span>
                  <span>·</span>
                  <span>{relTime(t.created_at)}</span>
                </div>
                {t.resident_name && (
                  <div className="text-xs text-white/60 mt-2">
                    Reported by <span className="text-white">{t.resident_name}</span>
                  </div>
                )}
                <div className="mt-2 flex items-center justify-end">
                  <ChevronRight className="h-4 w-4 text-white/40" />
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
